import { FieldValues } from "react-hook-form";

import { HUGGINGFACE_PROVIDER } from "@/components/ModelParameters/constants";
import { FieldType, StepType } from "@/components/Stepper/Step/types";
import { StepT } from "@/components/Stepper/Step/types";
import { Model, Provider } from "@/queries/models";

import { CustomParameters } from "./CustomParameters";
import { getIsModelExists, getModels, getProviders } from "./utils";
import { ProviderWithLogo } from "@/components/ProviderWithLogo";

const validateDisplayName = (
  value: string,
  models: Model[],
  isViewing: boolean,
  isEdit: boolean,
) => {
  if (value.length === 0) {
    return "This field is required";
  }

  const modelExists = getIsModelExists(models, value.toLowerCase().trim());
  if (modelExists && !isViewing && !isEdit) {
    return "This model name already exists";
  }

  return true;
};

export const getCustomModelCreationSteps = ({
  models,
  providers,
  isEdit = false,
  isViewing = false,
  isLoading = false,
}: {
  models: Model[];
  providers: Provider[];
  isEdit?: boolean;
  isViewing?: boolean;
  isLoading?: boolean;
}): StepT[] => [
  {
    id: "create_custom_model",
    title: isEdit ? "Edit Custom Model" : "Create Custom Model",
    description: isEdit
      ? "Edit the custom model to use in your workspace."
      : "Create a custom model to use in your workspace.",
    isOptional: false,
    type: StepType.FIELDS,
    options: [
      {
        label: "Provider",
        id: "provider",
        validation: (value: string) =>
          value.length > 0 || "This field is required",
        readOnly: isViewing,
        type: FieldType.AUTOCOMPLETE,
        fieldOptions: getProviders(providers, isEdit),
        renderOption: (option) => <ProviderWithLogo provider={option.value} />,
        placeholder: "Select provider",
        addNewOption: false,
      },
      {
        label: (fieldsInputs) => {
          const provider = fieldsInputs.provider;
          return provider === HUGGINGFACE_PROVIDER
            ? "Model Name or URL"
            : "Model Name";
        },
        id: "modelName",
        validation: (value: string) =>
          value.length > 0 || "This field is required",
        readOnly: isViewing,
        type: FieldType.AUTOCOMPLETE,
        getAddOptionText: (query: string) => `Add new model: ${query}`,
        fieldOptions: getModels(models),
        tooltip: "Select a known model or enter a custom model name",
        addNewOption: true,
        placeholder: (fieldsInputs) => {
          const provider = fieldsInputs.provider;
          return provider === HUGGINGFACE_PROVIDER
            ? "Enter inference client"
            : "Enter custom model name";
        },
      },
      {
        label: "API Key",
        id: "apiKey",
        tooltip: "Enter the API key for HuggingFace model",
        inputType: "password",
        readOnly: isViewing,
        showIf: (fieldsInputs) =>
          fieldsInputs.provider === HUGGINGFACE_PROVIDER,
        validation: (value: string) =>
          value.length > 0 || "This field is required",
        type: FieldType.TEXT,
        placeholder: "Enter API key",
      },
      {
        label: "Display Name",
        id: "displayName",
        readOnly: isViewing,
        tooltip:
          "This is human readable name will be shown when selecting a model in the prompt playground",
        validation: (value) =>
          validateDisplayName(value, models, isViewing, isEdit),
        type: FieldType.TEXT,
        placeholder: "Enter display name",
      },
      {
        label: "Model Type",
        id: "modelType",
        readOnly: isViewing,
        validation: (value) => value.length > 0 || "This field is required",
        type: FieldType.RADIO,
        fieldOptions: [
          {
            label: "Chat",
            value: "chat",
            id: "chat",
          },
          {
            label: "Completion",
            value: "completion",
            id: "completion",
          },
        ],
        placeholder: "Select model type",
      },
    ],
  },
  {
    id: "parameters",
    title: "Custom Model Parameters",
    type: StepType.CUSTOM_STEP,
    description: "Define the parameters for your custom model.",
    getCustomContent: (formValues: FieldValues) => (
      <CustomParameters
        isLoading={isLoading}
        provider={formValues.create_custom_model.provider}
      />
    ),
    isOptional: false,
  },
];
